import { useState, useRef, useEffect } from "react";
import { PropertyGroupMenu } from "@/components/form/PropertyGroupMenu";
import { X, Plus } from "lucide-react";

export function MultiSelect({ label, value = [], onChange, options = [], placeholder = "Type to search…", testid, onQuickAdd, propertyField, selectAll, onOpenItem, itemInfo }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [extra, setExtra] = useState([]);
  const [adding, setAdding] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const allOptions = [...options, ...extra];
  const selected = allOptions.filter((o) => value.includes(o.value));
  const query = q.trim();
  const searchOnly = options.some((o) => o.search);
  const needMore = searchOnly && query.length < 3;
  const available = needMore ? [] : allOptions.filter(
    (o) => !value.includes(o.value) && o.label.toLowerCase().includes(query.toLowerCase())
  );
  const canQuickAdd = Boolean(onQuickAdd) && query.length > 0 && !needMore && available.length === 0 &&
    !allOptions.some((o) => o.label.toLowerCase() === query.toLowerCase());

  const add = (v) => { onChange([...value, v]); setQ(""); setOpen(false); };
  const isPropertyField = propertyField || allOptions.some((o) => "dtr" in o);
  const remove = (v) => onChange(value.filter((x) => x !== v));

  const quickAdd = async () => {
    if (!query || adding) return;
    setAdding(true);
    try {
      const opt = await onQuickAdd(query);
      if (opt) {
        setExtra((e) => [...e, opt]);
        onChange([...value, opt.value]);
      }
      setQ("");
      setOpen(false);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div ref={ref}>
      {(label || isPropertyField || selectAll) && (
        <div className="mb-1.5 flex items-center justify-between gap-2">
          {label ? <label className="block text-xs font-semibold text-slate-600">{label}</label> : <span />}
          {isPropertyField && (
            <PropertyGroupMenu value={value} validIds={new Set(allOptions.map((o) => o.value))} onAdd={(ids) => ids.length && onChange([...value, ...ids])} testid={testid} />
          )}
          {selectAll && !isPropertyField && allOptions.length > 0 && (
            <button type="button" onClick={() => onChange(value.length === allOptions.length ? [] : allOptions.map((o) => o.value))} data-testid={`${testid}-select-all`}
              className="text-[11px] font-semibold text-sky-600 hover:underline">{value.length === allOptions.length ? "Clear all" : "Select all"}</button>
          )}
        </div>
      )}
      <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 transition-colors focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-100">
        <div className="flex flex-wrap items-center gap-1.5">
          {selected.map((o) => {
            const info = itemInfo?.(o.value);
            return (
            <span key={o.value} data-testid={`tag-${o.value}`} className="group/tag relative inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
              {onOpenItem ? <button type="button" onClick={() => onOpenItem(o.value)} className="hover:underline" title={info ? undefined : "Open"} data-testid={`tag-open-${o.value}`}>{o.label}</button> : o.label}
              <button type="button" onClick={() => remove(o.value)} className="rounded-full hover:text-sky-900" data-testid={`tag-remove-${o.value}`}>
                <X className="h-3 w-3" />
              </button>
              {info && (
                <span className="pointer-events-none absolute bottom-full left-0 z-50 mb-1.5 hidden w-max max-w-[18rem] rounded-lg bg-slate-900 px-2.5 py-1.5 text-[11px] font-normal leading-relaxed text-white shadow-lg group-hover/tag:block" data-testid={`tag-info-${o.value}`}>
                  {info.map((l) => <span key={l} className="block truncate">{l}</span>)}
                </span>
              )}
            </span>
            );
          })}
          <input
            data-testid={testid}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canQuickAdd && available.length === 0) { e.preventDefault(); quickAdd(); }
            }}
            placeholder={selected.length ? "" : placeholder}
            className="min-w-[100px] flex-1 border-0 bg-transparent py-1 text-sm outline-none placeholder:text-slate-400"
          />
        </div>
      </div>
      {open && needMore && (
        <div className="relative">
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-400 shadow-lg" data-testid={`${testid}-type-hint`}>
            Type at least 3 letters to search
          </div>
        </div>
      )}
      {open && (available.length > 0 || canQuickAdd) && (
        <div className="relative">
          <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            {(searchOnly ? available.slice(0, 50) : available).map((o) => (
              <button
                type="button"
                key={o.value}
                data-testid={`option-${o.value}`}
                onClick={() => add(o.value)}
                className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-sky-50"
              >
                {o.label}
              </button>
            ))}
            {canQuickAdd && (
              <button
                type="button"
                data-testid={`${testid}-quick-add`}
                onClick={quickAdd}
                disabled={adding}
                className="flex w-full items-center gap-1.5 border-t border-slate-100 px-3 py-2 text-left text-sm font-medium text-sky-600 hover:bg-sky-50 disabled:opacity-50"
              >
                <Plus className="h-3.5 w-3.5" /> {adding ? "Adding…" : <>Quick Add “{query}”</>}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
